import { Pencil, Trash2, Search, AlertCircle } from 'lucide-react';
import type { MessageSchedule } from '../../types/message';
import { formatDateTime } from '../../lib/formatters';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  EmptyState,
} from '../ui';

interface MessageListProps {
  messages: MessageSchedule[];
  onEdit: (message: MessageSchedule) => void;
  onDelete: (message: MessageSchedule) => void;
  isLoading?: boolean;
  isFiltered?: boolean;
}

function MessageList({ messages, onEdit, onDelete, isLoading, isFiltered }: MessageListProps) {
  const truncateMessage = (message: string, maxLength: number = 40) => {
    if (message.length <= maxLength) return message;
    return `${message.substring(0, maxLength)}...`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <EmptyState
        icon={<Search className="h-12 w-12" />}
        title={isFiltered ? 'Nenhuma mensagem encontrada' : 'Nenhuma mensagem agendada'}
        description={
          isFiltered
            ? 'Tente ajustar os filtros para encontrar o que procura'
            : 'Crie sua primeira mensagem agendada para começar'
        }
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Mensagem</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Prioridade</TableHead>
            <TableHead>Destinatário</TableHead>
            <TableHead>Agendamento</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {messages.map(message => (
            <TableRow key={message.id}>
              <TableCell className="max-w-xs">
                <div className="space-y-1">
                  <div className="truncate font-medium">{truncateMessage(message.message)}</div>
                  {message.title && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">{message.title}</div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <Badge variant={message.send ? 'success' : 'warning'}>
                    {message.send ? 'Enviado' : 'Pendente'}
                  </Badge>
                  {message.close_ticket && (
                    <Badge variant="info" className="text-xs">
                      Fechar Ticket
                    </Badge>
                  )}
                  {message.send_error && (
                    <div className="mt-1 flex items-start gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span className="break-words">{message.send_error}</span>
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <span className="text-sm">{message.priority || 99}</span>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  {message.contact_name && (
                    <div className="text-sm font-medium">{message.contact_name}</div>
                  )}
                  {message.sendtoPhone && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {message.sendtoPhone}
                    </div>
                  )}
                  {message.sendToMail && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {message.sendToMail}
                    </div>
                  )}
                  {!message.contact_name && !message.sendtoPhone && !message.sendToMail && (
                    <span className="text-gray-400">-</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {message.scheduled_at ? formatDateTime(message.scheduled_at) : '-'}
                </div>
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(message)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(message)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default MessageList;
